import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { resolveThumbnailUrl } from '../../lib/publicApi';
import {
  ArrowLeft, GripVertical, ImageIcon, Loader2, Lock, Package, Plus, Save, Upload, Video, X,
} from 'lucide-react';
import AdminProductCategoriesPanel from './AdminProductCategoriesPanel';

const slugify = (value) =>
  (value || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

const minorToNgnDisplay = (minor) => (minor == null ? '' : String(minor / 100));
const minorToUsdDisplay = (minor) => (minor == null ? '' : (minor / 100).toFixed(2));
const ngnDisplayToMinor = (value) => Math.round(Number(value || 0) * 100);
const usdDisplayToMinor = (value) => Math.round(Number(value || 0) * 100);

const formatMoney = (minor, currency) => {
  if (currency === 'NGN') return `₦${(minor / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  return `$${(minor / 100).toFixed(2)}`;
};


function GalleryFileRow({ item, index, onMoveUp, onMoveDown, onRemove, total }) {
  return (
    <div
      className="flex items-center gap-3 rounded-lg border border-border bg-muted/30 p-3"
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', String(index));
        e.dataTransfer.effectAllowed = 'move';
      }}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        const from = Number(e.dataTransfer.getData('text/plain'));
        if (!Number.isNaN(from) && from !== index) onMoveUp(from, index);
      }}
    >
      <GripVertical className="h-4 w-4 shrink-0 text-muted-foreground cursor-grab" />
      {item.preview ? (
        <img src={item.preview} alt="" className="h-14 w-14 rounded object-cover border" />
      ) : (
        <div className="h-14 w-14 rounded bg-muted flex items-center justify-center">
          <ImageIcon className="h-5 w-5 text-muted-foreground" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{item.file?.name || item.label}</p>
        <p className="text-xs text-muted-foreground">Gallery position {index + 1}</p>
      </div>
      <div className="flex gap-1">
        <Button type="button" variant="outline" size="sm" disabled={index === 0} onClick={() => onMoveUp(index, index - 1)}>↑</Button>
        <Button type="button" variant="outline" size="sm" disabled={index >= total - 1} onClick={() => onMoveDown(index, index + 1)}>↓</Button>
        {onRemove && (
          <Button type="button" variant="ghost" size="sm" onClick={() => onRemove(index)}>Remove</Button>
        )}
      </div>
    </div>
  );
}

function ProductList({ products, categoryFilter, onCategoryFilter, onCreate, onEdit, onToggleActive, togglingSlug }) {
  const filtered = useMemo(() => {
    if (categoryFilter === 'all') return products;
    return products.filter((p) => p.category === categoryFilter);
  }, [products, categoryFilter]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Digital Products</h2>
          <p className="text-sm text-muted-foreground">Manage templates and toolkits sold outside the credit system.</p>
        </div>
        <Button onClick={onCreate} data-testid="digital-products-new-btn">
          <Plus className="mr-2 h-4 w-4" />
          New Product
        </Button>
      </div>

      <div className="flex gap-2">
        {[
          { id: 'all', label: 'All' },
          { id: 'template', label: 'Templates' },
          { id: 'toolkit', label: 'Toolkits' },
        ].map((opt) => (
          <Button
            key={opt.id}
            variant={categoryFilter === opt.id ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => onCategoryFilter(opt.id)}
            data-testid={`digital-products-filter-${opt.id}`}
          >
            {opt.label}
          </Button>
        ))}
      </div>

      <Card className="card-elevated">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">Thumb</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>NGN</TableHead>
                <TableHead>USD</TableHead>
                <TableHead>Active</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((product) => (
                <TableRow key={product.slug} data-testid={`digital-product-row-${product.slug}`}>
                  <TableCell>
                    {product.thumbnail_signed_url ? (
                      <img src={resolveThumbnailUrl(product.thumbnail_signed_url)} alt="" className="h-10 w-10 rounded object-cover border" />
                    ) : (
                      <div className="h-10 w-10 rounded bg-muted flex items-center justify-center">
                        <Package className="h-4 w-4 text-muted-foreground" />
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="font-medium">{product.name}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{product.slug}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{product.category}</Badge>
                  </TableCell>
                  <TableCell>{formatMoney(product.price_ngn, 'NGN')}</TableCell>
                  <TableCell>{formatMoney(product.price_usd, 'USD')}</TableCell>
                  <TableCell>
                    <Switch
                      checked={!!product.is_active}
                      disabled={togglingSlug === product.slug}
                      onCheckedChange={(checked) => onToggleActive(product, checked)}
                      data-testid={`digital-product-active-${product.slug}`}
                    />
                  </TableCell>
                  <TableCell>
                    <Button variant="outline" size="sm" onClick={() => onEdit(product.slug)} data-testid={`digital-product-edit-${product.slug}`}>
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!filtered.length && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                    No digital products found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function ProductEditForm({ product, api, saving, assetBusy, onSave, onBack, onProductUpdated, setAssetBusy, subcategoryOptions }) {
  const galleryInputRef = useRef(null);
  const [form, setForm] = useState({
    name: product.name || '',
    subcategory: product.subcategory || 'other',
    description: product.description || '',
    priceNgn: minorToNgnDisplay(product.price_ngn),
    priceUsd: minorToUsdDisplay(product.price_usd),
    featuresText: (product.features || []).join('\n'),
    is_active: !!product.is_active,
  });

  useEffect(() => {
    setForm({
      name: product.name || '',
      subcategory: product.subcategory || 'other',
      description: product.description || '',
      priceNgn: minorToNgnDisplay(product.price_ngn),
      priceUsd: minorToUsdDisplay(product.price_usd),
      featuresText: (product.features || []).join('\n'),
      is_active: !!product.is_active,
    });
  }, [product]);

  const galleryVideo = (product.gallery || []).find((g) => g.type === 'video');
  const galleryImages = useMemo(
    () => [...(product.gallery || [])]
      .filter((g) => g.type === 'image')
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [product.gallery],
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      name: form.name.trim(),
      subcategory: form.subcategory,
      description: form.description.trim(),
      price_ngn: ngnDisplayToMinor(form.priceNgn),
      price_usd: usdDisplayToMinor(form.priceUsd),
      features: form.featuresText.split('\n').map((line) => line.trim()).filter(Boolean),
      is_active: form.is_active,
    });
  };

  const refreshProduct = async () => {
    const res = await api.get(`/admin/digital-products/${product.slug}`);
    onProductUpdated(res.data);
  };

  const handleAddImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setAssetBusy(true);
    try {
      const formData = new FormData();
      files.forEach((file) => formData.append('gallery_images', file));
      await api.post(`/admin/digital-products/${product.slug}/gallery`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await refreshProduct();
      toast.success('Gallery images added');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add gallery images');
    } finally {
      setAssetBusy(false);
      e.target.value = '';
    }
  };

  const handleDeleteImage = async (imageId) => {
    setAssetBusy(true);
    try {
      await api.delete(`/admin/digital-products/${product.slug}/gallery/${imageId}`);
      await refreshProduct();
      toast.success('Gallery image removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove gallery image');
    } finally {
      setAssetBusy(false);
    }
  };

  const applyGalleryOrder = async (imageIds, successMessage = 'Gallery order updated') => {
    setAssetBusy(true);
    try {
      await api.patch(`/admin/digital-products/${product.slug}/gallery/reorder`, { image_ids: imageIds });
      await refreshProduct();
      toast.success(successMessage);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reorder gallery');
    } finally {
      setAssetBusy(false);
    }
  };

  const moveGalleryImage = async (from, to) => {
    if (from === to || to < 0 || to >= galleryImages.length) return;
    const ids = galleryImages.map((item) => item.image_id);
    const next = [...ids];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    await applyGalleryOrder(next);
  };

  const setAsThumbnail = async (imageId) => {
    const ids = galleryImages.map((item) => item.image_id);
    const index = ids.indexOf(imageId);
    if (index <= 0) return;
    const next = [imageId, ...ids.filter((id) => id !== imageId)];
    await applyGalleryOrder(next, 'Storefront thumbnail updated');
  };

  const handleReplaceGalleryImage = async (imageId, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!window.confirm('Replace this gallery image? Storefront previews will refresh automatically.')) {
      e.target.value = '';
      return;
    }
    setAssetBusy(true);
    try {
      const formData = new FormData();
      formData.append('gallery_image', file);
      const res = await api.patch(
        `/admin/digital-products/${product.slug}/gallery/${imageId}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      onProductUpdated(res.data);
      toast.success('Gallery image replaced');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to replace gallery image');
    } finally {
      setAssetBusy(false);
      e.target.value = '';
    }
  };

  const handleReplaceZip = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!window.confirm('This replaces the file buyers download. Continue?')) {
      e.target.value = '';
      return;
    }
    setAssetBusy(true);
    try {
      const formData = new FormData();
      formData.append('download_zip', file);
      const res = await api.patch(`/admin/digital-products/${product.slug}/download`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onProductUpdated(res.data);
      toast.success('Download ZIP replaced');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to replace ZIP');
    } finally {
      setAssetBusy(false);
      e.target.value = '';
    }
  };

  const handleReplaceDemo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const label = galleryVideo ? 'Replace demo video' : 'Add demo video';
    if (!window.confirm(`${label} for this product. Continue?`)) {
      e.target.value = '';
      return;
    }
    setAssetBusy(true);
    try {
      const formData = new FormData();
      formData.append('demo_video', file);
      const res = await api.patch(`/admin/digital-products/${product.slug}/demo-video`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onProductUpdated(res.data);
      toast.success(galleryVideo ? 'Demo video replaced' : 'Demo video added');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update demo video');
    } finally {
      setAssetBusy(false);
      e.target.value = '';
    }
  };

  const renderImageTile = (item, {
    index = 0,
    total = 1,
  } = {}) => (
    <div
      key={item.image_id || item.s3_key}
      className={`rounded border overflow-hidden relative ${item.locked ? 'ring-1 ring-slate-200' : 'ring-1 ring-orange-200'}`}
    >
      {item.signed_url ? (
        <img
          key={item.signed_url}
          src={resolveThumbnailUrl(item.signed_url)}
          alt=""
          className="h-24 w-full object-cover"
        />
      ) : (
        <div className="h-24 bg-muted" />
      )}
      <div className="flex items-center justify-between gap-1 p-1">
        <p className="text-[10px] truncate text-muted-foreground flex-1">{item.s3_key.split('/').pop()}</p>
        <div className="flex items-center gap-1 shrink-0">
          {index === 0 && (
            <Badge variant="default" className="text-[10px]">Thumbnail</Badge>
          )}
          {item.locked ? (
            <Badge variant="secondary" className="text-[10px] gap-1">
              <Lock className="h-3 w-3" /> Original
            </Badge>
          ) : (
            <Button type="button" variant="ghost" size="icon" className="h-6 w-6" disabled={assetBusy} onClick={() => handleDeleteImage(item.image_id)}>
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
      {item.locked && (
        <div className="px-1 pb-1">
          <Input
            type="file"
            accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
            disabled={assetBusy}
            onChange={(e) => handleReplaceGalleryImage(item.image_id, e)}
            data-testid={`digital-product-replace-gallery-${item.image_id}`}
            className="text-[10px] h-8"
          />
          <p className="text-[10px] text-muted-foreground mt-0.5">Replace overwrites the existing S3 object in place.</p>
        </div>
      )}
      {total > 1 && (
        <div className="flex flex-wrap justify-end gap-1 px-1 pb-1">
          {index > 0 && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="h-6 px-2 text-[10px]"
              disabled={assetBusy}
              onClick={() => setAsThumbnail(item.image_id)}
              data-testid={`digital-product-thumbnail-${item.image_id}`}
            >
              Set thumbnail
            </Button>
          )}
          <Button type="button" variant="outline" size="sm" className="h-6 px-2" disabled={assetBusy || index === 0} onClick={() => moveGalleryImage(index, index - 1)}>↑</Button>
          <Button type="button" variant="outline" size="sm" className="h-6 px-2" disabled={assetBusy || index >= total - 1} onClick={() => moveGalleryImage(index, index + 1)}>↓</Button>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="mr-1 h-4 w-4" /> Back
        </Button>
        <div>
          <h2 className="text-xl font-semibold">Edit Product</h2>
          <p className="text-sm text-muted-foreground">{product.slug}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="grid gap-6 lg:grid-cols-2">
        <Card className="card-elevated">
          <CardHeader>
            <CardTitle>Editable fields</CardTitle>
            <CardDescription>Slug and category are identity fields and cannot change here.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Slug (read-only)</Label>
                <Input value={product.slug} readOnly disabled />
              </div>
              <div>
                <Label>Category (read-only)</Label>
                <Input value={product.category} readOnly disabled className="capitalize" />
              </div>
            </div>
            <div>
              <Label htmlFor="dp-name">Name</Label>
              <Input id="dp-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </div>
            <div>
              <Label>Subcategory</Label>
              <Select value={form.subcategory} onValueChange={(value) => setForm({ ...form, subcategory: value })}>
                <SelectTrigger data-testid="digital-product-edit-subcategory">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {subcategoryOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="dp-desc">Description</Label>
              <Textarea id="dp-desc" rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="dp-ngn">Price (NGN)</Label>
                <Input id="dp-ngn" type="number" min="0" step="1" value={form.priceNgn} onChange={(e) => setForm({ ...form, priceNgn: e.target.value })} />
                <p className="text-xs text-muted-foreground mt-1">Whole naira (e.g. 950 = ₦950.00)</p>
              </div>
              <div>
                <Label htmlFor="dp-usd">Price (USD)</Label>
                <Input id="dp-usd" type="number" min="0" step="0.01" value={form.priceUsd} onChange={(e) => setForm({ ...form, priceUsd: e.target.value })} />
                <p className="text-xs text-muted-foreground mt-1">Dollars and cents (e.g. 1.50)</p>
              </div>
            </div>
            <div>
              <Label htmlFor="dp-features">Features (one per line)</Label>
              <Textarea id="dp-features" rows={4} value={form.featuresText} onChange={(e) => setForm({ ...form, featuresText: e.target.value })} placeholder="Optional — for future What's Included section" />
            </div>
            <div className="flex items-center justify-between rounded-lg border p-4">
              <div>
                <Label>Active (published)</Label>
                <p className="text-xs text-muted-foreground">When on, product will be buyer-visible once the storefront ships.</p>
              </div>
              <Switch checked={form.is_active} onCheckedChange={(checked) => setForm({ ...form, is_active: checked })} />
            </div>
            <Button type="submit" disabled={saving || assetBusy} data-testid="digital-product-save-btn">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Save changes
            </Button>
          </CardContent>
        </Card>

        <Card className="card-elevated">
          <CardHeader>
            <CardTitle>Assets</CardTitle>
            <CardDescription>All gallery images share one order — use ↑/↓ or Set thumbnail to pick the storefront preview (first image; demo video stays separate at the top when present). Original uploads cannot be deleted but can be replaced in place.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="space-y-2">
              <Label>Download zip</Label>
              <p className="text-sm font-mono break-all">{product.download_s3_key || '—'}</p>
              <div>
                <Input type="file" accept=".zip,application/zip" disabled={assetBusy} onChange={handleReplaceZip} data-testid="digital-product-replace-zip" />
                <p className="text-xs text-muted-foreground mt-1">
                Replace uploads a new ZIP object and updates the product key (buyers always get the latest file).
              </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Demo video</Label>
              {galleryVideo ? (
                <p className="text-sm font-mono break-all">{galleryVideo.s3_key}</p>
              ) : (
                <p className="text-sm text-muted-foreground">No demo video yet.</p>
              )}
              <div>
                <Input type="file" accept=".mp4,video/mp4" disabled={assetBusy} onChange={handleReplaceDemo} data-testid="digital-product-replace-demo" />
                <p className="text-xs text-muted-foreground mt-1">
                  {galleryVideo ? 'Replace Video' : 'Add Demo Video'} — new uploads become gallery[0] when none exists.
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <Label>Gallery</Label>
              {galleryVideo && (
                <div className="rounded border overflow-hidden max-w-[160px]">
                  {galleryVideo.signed_url ? (
                    <video
                      key={galleryVideo.signed_url}
                      src={resolveThumbnailUrl(galleryVideo.signed_url)}
                      controls
                      muted
                      className="h-24 w-full bg-black object-cover"
                    />
                  ) : (
                    <div className="h-24 bg-muted flex flex-col items-center justify-center text-xs text-muted-foreground">
                      <Video className="h-5 w-5 mb-1" /> demo.mp4
                    </div>
                  )}
                  <p className="text-[10px] p-1 truncate text-muted-foreground">{galleryVideo.s3_key.split('/').pop()}</p>
                </div>
              )}
              {galleryImages.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-2">
                    Gallery images (top = storefront thumbnail)
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {galleryImages.map((item, index) => renderImageTile(item, {
                      index,
                      total: galleryImages.length,
                    }))}
                  </div>
                </div>
              )}
              <div>
                <input
                  ref={galleryInputRef}
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                  multiple
                  disabled={assetBusy}
                  onChange={handleAddImages}
                  className="hidden"
                  data-testid="digital-product-add-gallery"
                />
                <Button type="button" variant="outline" size="sm" disabled={assetBusy} onClick={() => galleryInputRef.current?.click()}>
                  <Plus className="mr-2 h-4 w-4" /> Add Image
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}

function ProductCreateForm({ saving, uploadProgress, onSubmit, onBack, subcategoryOptions }) {
  const [category, setCategory] = useState('template');
  const [subcategory, setSubcategory] = useState('');
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [description, setDescription] = useState('');
  const [priceNgn, setPriceNgn] = useState('');
  const [priceUsd, setPriceUsd] = useState('');
  const [galleryItems, setGalleryItems] = useState([]);
  const [zipFile, setZipFile] = useState(null);
  const [demoFile, setDemoFile] = useState(null);

  useEffect(() => {
    if (!slugTouched) setSlug(slugify(name));
  }, [name, slugTouched]);

  const reorderGallery = (from, to) => {
    setGalleryItems((items) => {
      const next = [...items];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  const handleGalleryPick = (e) => {
    const files = Array.from(e.target.files || []);
    const mapped = files.map((file) => ({
      id: `${file.name}-${file.size}-${Math.random()}`,
      file,
      preview: URL.createObjectURL(file),
    }));
    setGalleryItems((prev) => [...prev, ...mapped]);
    e.target.value = '';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!galleryItems.length) {
      toast.error('Add at least one gallery image');
      return;
    }
    if (!zipFile) {
      toast.error('Select a download ZIP file');
      return;
    }
    if (!subcategory) {
      toast.error('Select a subcategory');
      return;
    }
    onSubmit({
      category,
      subcategory,
      slug: slug.trim(),
      name: name.trim(),
      description: description.trim(),
      price_ngn: ngnDisplayToMinor(priceNgn),
      price_usd: usdDisplayToMinor(priceUsd),
      galleryItems,
      zipFile,
      demoFile,
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="mr-1 h-4 w-4" /> Back
        </Button>
        <div>
          <h2 className="text-xl font-semibold">New Digital Product</h2>
          <p className="text-sm text-muted-foreground">Upload assets and catalog metadata. New products start inactive.</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card className="card-elevated border-orange-200 bg-orange-50/40">
          <CardContent className="pt-6 text-sm text-muted-foreground">
            Product will be created as <strong>inactive</strong>. Toggle Active on the edit page when ready to publish.
          </CardContent>
        </Card>

        <div className="grid gap-6 lg:grid-cols-2">
          <Card className="card-elevated">
            <CardHeader><CardTitle>Details</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Category</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger data-testid="digital-product-create-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="template">Template</SelectItem>
                    <SelectItem value="toolkit">Toolkit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Subcategory</Label>
                <Select value={subcategory} onValueChange={setSubcategory} required>
                  <SelectTrigger data-testid="digital-product-create-subcategory">
                    <SelectValue placeholder="Select subcategory" />
                  </SelectTrigger>
                  <SelectContent>
                    {subcategoryOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="create-name">Name</Label>
                <Input id="create-name" value={name} onChange={(e) => setName(e.target.value)} required data-testid="digital-product-create-name" />
              </div>
              <div>
                <Label htmlFor="create-slug">Slug</Label>
                <Input
                  id="create-slug"
                  value={slug}
                  onChange={(e) => { setSlugTouched(true); setSlug(slugify(e.target.value)); }}
                  required
                  data-testid="digital-product-create-slug"
                />
                <p className="text-xs text-muted-foreground mt-1">Auto-generated from name; editable before submit.</p>
              </div>
              <div>
                <Label htmlFor="create-desc">Description</Label>
                <Textarea id="create-desc" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="create-ngn">Price (NGN)</Label>
                  <Input id="create-ngn" type="number" min="0" step="1" value={priceNgn} onChange={(e) => setPriceNgn(e.target.value)} required />
                </div>
                <div>
                  <Label htmlFor="create-usd">Price (USD)</Label>
                  <Input id="create-usd" type="number" min="0" step="0.01" value={priceUsd} onChange={(e) => setPriceUsd(e.target.value)} required />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-elevated">
            <CardHeader><CardTitle>Assets</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Gallery images</Label>
                <Input type="file" accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp" multiple onChange={handleGalleryPick} data-testid="digital-product-gallery-input" />
                <p className="text-xs text-muted-foreground mt-1">Drag or use arrows to reorder before upload.</p>
                <div className="space-y-2 mt-3">
                  {galleryItems.map((item, index) => (
                    <GalleryFileRow
                      key={item.id}
                      item={item}
                      index={index}
                      total={galleryItems.length}
                      onMoveUp={reorderGallery}
                      onMoveDown={reorderGallery}
                      onRemove={(i) => setGalleryItems((prev) => prev.filter((_, idx) => idx !== i))}
                    />
                  ))}
                </div>
              </div>
              <div>
                <Label>Download ZIP</Label>
                <Input type="file" accept=".zip,application/zip" onChange={(e) => setZipFile(e.target.files?.[0] || null)} data-testid="digital-product-zip-input" />
                {zipFile && <p className="text-xs mt-1 text-muted-foreground">Selected: {zipFile.name}</p>}
              </div>
              <div>
                <Label>Demo video (optional MP4)</Label>
                <Input type="file" accept=".mp4,video/mp4" onChange={(e) => setDemoFile(e.target.files?.[0] || null)} data-testid="digital-product-demo-input" />
                {demoFile && <p className="text-xs mt-1 text-muted-foreground">Selected: {demoFile.name}</p>}
              </div>
            </CardContent>
          </Card>
        </div>

        {uploadProgress != null && (
          <div className="rounded-lg border p-4">
            <div className="flex justify-between text-sm mb-2">
              <span>Uploading…</span>
              <span>{uploadProgress}%</span>
            </div>
            <div className="h-2 rounded-full bg-muted overflow-hidden">
              <div className="h-full bg-primary transition-all" style={{ width: `${uploadProgress}%` }} />
            </div>
          </div>
        )}

        <Button type="submit" disabled={saving} className="w-full sm:w-auto" data-testid="digital-product-create-submit">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
          Create product
        </Button>
      </form>
    </div>
  );
}

export default function AdminDigitalProductsPanel() {
  const { api } = useAuth();
  const [section, setSection] = useState('products');
  const [view, setView] = useState('list');
  const [storeCategories, setStoreCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [saving, setSaving] = useState(false);
  const [assetBusy, setAssetBusy] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [togglingSlug, setTogglingSlug] = useState(null);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/digital-products');
      setProducts(res.data.products || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load digital products');
    } finally {
      setLoading(false);
    }
  }, [api]);

  const loadStoreCategories = useCallback(async () => {
    try {
      const res = await api.get('/admin/product-categories');
      setStoreCategories(res.data.categories || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load store categories');
    }
  }, [api]);

  const subcategoryOptions = useMemo(
    () => storeCategories.map((cat) => ({ value: cat.id, label: cat.name })),
    [storeCategories],
  );

  const loadProduct = useCallback(async (slug) => {
    const res = await api.get(`/admin/digital-products/${slug}`);
    setSelectedProduct(res.data);
  }, [api]);

  useEffect(() => {
    loadProducts();
    loadStoreCategories();
  }, [loadProducts, loadStoreCategories]);

  useEffect(() => {
    if (section === 'products') {
      loadStoreCategories();
    }
  }, [section, loadStoreCategories]);

  useEffect(() => {
    if (view === 'edit' && selectedSlug) {
      loadProduct(selectedSlug).catch((err) => {
        toast.error(err.response?.data?.detail || 'Failed to load product');
        setView('list');
      });
    }
  }, [view, selectedSlug, loadProduct]);

  const handleToggleActive = async (product, checked) => {
    setTogglingSlug(product.slug);
    try {
      await api.patch(`/admin/digital-products/${product.slug}`, { is_active: checked });
      setProducts((prev) => prev.map((p) => (p.slug === product.slug ? { ...p, is_active: checked } : p)));
      toast.success(checked ? 'Product activated' : 'Product deactivated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    } finally {
      setTogglingSlug(null);
    }
  };

  const handleSaveEdit = async (payload) => {
    setSaving(true);
    try {
      const res = await api.patch(`/admin/digital-products/${selectedSlug}`, payload);
      setSelectedProduct(res.data);
      await loadProducts();
      toast.success('Product updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async ({ galleryItems, zipFile, demoFile, ...fields }) => {
    setSaving(true);
    setUploadProgress(0);
    try {
      const formData = new FormData();
      formData.append('category', fields.category);
      formData.append('subcategory', fields.subcategory);
      formData.append('slug', fields.slug);
      formData.append('name', fields.name);
      formData.append('description', fields.description);
      formData.append('price_ngn', String(fields.price_ngn));
      formData.append('price_usd', String(fields.price_usd));
      galleryItems.forEach((item) => formData.append('gallery_images', item.file));
      formData.append('download_zip', zipFile);
      if (demoFile) formData.append('demo_video', demoFile);

      const res = await api.post('/admin/digital-products', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (evt) => {
          if (evt.total) setUploadProgress(Math.round((evt.loaded * 100) / evt.total));
        },
      });
      toast.success('Product created (inactive by default)');
      await loadProducts();
      setSelectedSlug(res.data.slug);
      setView('edit');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create product');
    } finally {
      setSaving(false);
      setUploadProgress(null);
    }
  };

  if (loading && view === 'list' && !products.length) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (section === 'categories') {
    return (
      <div className="space-y-4">
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setSection('products')} data-testid="digital-products-tab-products">Products</Button>
          <Button variant="secondary" size="sm" onClick={() => setSection('categories')} data-testid="digital-products-tab-categories">Categories</Button>
        </div>
        <AdminProductCategoriesPanel />
      </div>
    );
  }

  if (view === 'create') {
    return (
      <ProductCreateForm
        saving={saving}
        uploadProgress={uploadProgress}
        onSubmit={handleCreate}
        onBack={() => setView('list')}
        subcategoryOptions={subcategoryOptions}
      />
    );
  }

  if (view === 'edit' && selectedProduct) {
    return (
      <ProductEditForm
        product={selectedProduct}
        api={api}
        saving={saving}
        assetBusy={assetBusy}
        setAssetBusy={setAssetBusy}
        onSave={handleSaveEdit}
        onProductUpdated={setSelectedProduct}
        onBack={() => { setView('list'); setSelectedSlug(null); setSelectedProduct(null); }}
        subcategoryOptions={subcategoryOptions}
      />
    );
  }

  if (view === 'edit' && !selectedProduct) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Button variant="secondary" size="sm" onClick={() => setSection('products')} data-testid="digital-products-tab-products">Products</Button>
        <Button variant="outline" size="sm" onClick={() => setSection('categories')} data-testid="digital-products-tab-categories">Categories</Button>
      </div>
      <ProductList
        products={products}
        categoryFilter={categoryFilter}
        onCategoryFilter={setCategoryFilter}
        onCreate={() => setView('create')}
        onEdit={(slug) => { setSelectedSlug(slug); setView('edit'); }}
        onToggleActive={handleToggleActive}
        togglingSlug={togglingSlug}
      />
    </div>
  );
}
